import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
} from "@angular/common/http";
import { environment } from "../../environments/environment";
import { FileDetails } from "../components/files/files.component";
import { FileProgress } from "../models/task.models";
import { DialogService } from "./dialog.service";

@Injectable() // component-scoped — add to providers: [] in the component decorator
export class FileUploadService {
  progress: Record<string, FileProgress> = {};

  constructor(
    private http: HttpClient,
    private dialogService: DialogService,
  ) {}

  /** Pre-populate an existing file as already uploaded (used in edit-task mode). */
  initProgressForExistingFile(fileName: string, fileSize: any): void {
    this.progress[fileName] = {
      percent: 100,
      state: "done",
      sizeLabel: fileSize,
      etaLabel: "",
      size: 0,
      loaded: 0,
      lastLoaded: 0,
      lastTs: Date.now(),
      speedBps: 0,
    };
  }

  addRemoteUpload(fileName: string, fileSize: number = 0): void {
    // Don't create duplicate cards
    if (this.progress[fileName]) {
      return;
    }

    this.progress[fileName] = {
      percent: 100,
      state: "done",
      sizeLabel: this.formatFileSize(fileSize),
      etaLabel: "Completed",
      size: fileSize,
      loaded: fileSize,
      lastLoaded: fileSize,
      lastTs: Date.now(),
      speedBps: 0,
    };
  }

  uploadWithProgress(
    file: File,
    allowMode: string = "confirm",
    taskId: number,
    fileDetailsScan: FileDetails[],
    originalFileName: string,
    onRemoveFile: (name: string) => void,
  ): void {
    const url = `${environment.apiUrl}/tasks/${taskId}/files?allow=${allowMode}`;
    const form = new FormData();
    form.append("file", file);
    const safe = this.sanitizeName(file.name);
    const headers = new HttpHeaders().set("X-Digital-FileName", safe);

    if (allowMode === "confirm") {
      this.progress[safe] = {
        percent: 0,
        state: "uploading",
        size: file.size,
        sizeLabel: this.formatBytes(file.size),
        loaded: 0,
        lastLoaded: 0,
        lastTs: 0,
        speedBps: 0,
      };
    } else {
      const e = this.progress[safe];
      if (e) e.state = "uploading";
    }

    const req = new HttpRequest("POST", url, form, {
      reportProgress: true,
      responseType: "text",
      headers,
    });

    this.http.request(req).subscribe({
      next: (event) => {
        if (
          event.type === HttpEventType.UploadProgress &&
          (event as any).total
        ) {
          const { loaded, total } = event as any;
          const e = this.progress[safe];
          if (!e) return;
          e.loaded = loaded;
          e.percent = Math.min(Math.round((loaded / total) * 100), 92);
          this.updateEta(e, total);
          if (loaded >= total && e.state !== "waiting") {
            e.state = "waiting";
            this.startTrickle(safe, 99, 1, 400);
          }
        } else if (event.type === HttpEventType.Response) {
          const e = this.progress[safe];
          const res = JSON.parse(event.body as string);

          if (
            res.fileName === "You are about to upload file with same content"
          ) {
            this.stopTrickle(safe);
            this.dialogService
              .openConfirmDialog(
                "File already exists:",
                `File name: ${originalFileName}. Do you want to upload another copy?`,
              )
              .subscribe((confirmed: boolean) => {
                if (confirmed) {
                  this.uploadWithProgress(
                    file,
                    "allow",
                    taskId,
                    fileDetailsScan,
                    originalFileName,
                    onRemoveFile,
                  );
                } else {
                  this.removeProgress(safe);
                }
              });
            return;
          }

          if (res.fileName === "Duplicate file content detected.") {
            this.stopTrickle(safe);
            this.dialogService
              .openMessageDialog(
                "Duplicate Content Found",
                "This file content already exists.",
              )
              .subscribe((confirmed: boolean) => {
                if (confirmed) {
                  this.uploadWithProgress(
                    file,
                    "allow",
                    taskId,
                    fileDetailsScan,
                    originalFileName,
                    onRemoveFile,
                  );
                } else {
                  this.removeProgress(safe);
                  onRemoveFile(file.name);
                }
              });
            return;
          }

          if (e) {
            this.stopTrickle(safe);
            e.percent = 100;
            e.state = "done";
            e.etaLabel = "Completed";
            if (res.fileName && res.fileName !== safe) {
              this.progress[res.fileName] = { ...e };
              delete this.progress[safe];
            }
          }

          fileDetailsScan.push({
            fileId: 0,
            fileType: "",
            fileName: res.fileName,
            fileSize: file.size,
          });
        }
      },
      error: (err) => {
        this.dialogService.openMessageDialog(
          "Error",
          err?.error?.message || "Upload failed.",
        );
        const e = this.progress[safe];
        if (e) {
          this.stopTrickle(safe);
          e.state = "error";
        }
      },
    });
  }

  removeProgress(name: string): void {
    delete this.progress[name];
  }

  formatFileSize(bytes: number): string {
    if (bytes === null || bytes === undefined || isNaN(bytes)) return "";
    const MB = 1048576;
    const KB = 1024;
    return bytes >= MB
      ? `${(bytes / MB).toFixed(2)} MB`
      : `${(bytes / KB).toFixed(2)} KB`;
  }

  formatBytes(b: number): string {
    if (!isFinite(b)) return "";
    const units = ["B", "KB", "MB", "GB", "TB"];
    let i = 0;
    let n = b;
    while (n >= 1024 && i < units.length - 1) {
      n /= 1024;
      i++;
    }
    return `${n.toFixed(i ? 2 : 0)}${units[i]}`;
  }

  private sanitizeName(name: string): string {
    return name.replace(/[^a-zA-Z0-9.]/g, "_");
  }

  private formatDuration(sec: number): string {
    if (!isFinite(sec) || sec < 0) return "";
    if (sec < 1) return "1 second left";
    if (sec < 60) return `${Math.max(1, Math.round(sec))} seconds left`;
    const m = Math.floor(sec / 60);
    const s = Math.round(sec % 60);
    return s > 0 ? `${m}m ${s}s left` : `${m}m left`;
  }

  private startTrickle(name: string, to = 99, step = 1, ms = 500): void {
    const entry = this.progress[name];
    if (!entry) return;
    this.stopTrickle(name);
    entry.intervalId = setInterval(() => {
      const e = this.progress[name];
      if (!e || e.state === "done" || e.state === "error")
        return this.stopTrickle(name);
      if (e.percent < to) e.percent += step;
    }, ms);
  }

  private stopTrickle(name: string): void {
    const entry = this.progress[name];
    if (entry?.intervalId) {
      clearInterval(entry.intervalId);
      entry.intervalId = undefined;
    }
  }

  private updateEta(entry: FileProgress, total: number): void {
    const now = performance.now();
    if (entry.lastTs) {
      const dt = (now - entry.lastTs) / 1000;
      const dbytes = Math.max(0, entry.loaded - entry.lastLoaded);
      const inst = dbytes / Math.max(0.001, dt);
      const alpha = 0.25;
      entry.speedBps = entry.speedBps
        ? alpha * inst + (1 - alpha) * entry.speedBps
        : inst;
    }
    entry.lastTs = now;
    entry.lastLoaded = entry.loaded;
    if (total && entry.speedBps > 0) {
      const remain = Math.max(0, total - entry.loaded);
      entry.etaSeconds = remain / entry.speedBps;
      entry.etaLabel = this.formatDuration(entry.etaSeconds);
    } else {
      entry.etaSeconds = undefined;
      entry.etaLabel = "calculating…";
    }
  }
}
